import React from 'react';
import { styled } from '@mui/material';
import { CustomChartLabel } from '../../../components/Widgets/CustomChartLabel';
import type { ArcAngles } from './PieSlice.utils';
import { computeTagLabelPosition, createLabelRefHandler, TAG_LABEL_INITIAL_WIDTH } from './tagLabel.utils';

export const tagLabelClassNames = {
  root: 'Chart-PieTagLabel',
  content: 'Chart-PieTagLabelContent',
} as const;

type TagLabelProps = {
  label: string | number;
  angles: ArcAngles;
  ringInnerRadius: number;
  ringOuterRadius: number;
  highlighted: boolean;
  selected: boolean;
  color: string;
};

export const TagLabel = ({ label, angles, ringInnerRadius, ringOuterRadius, highlighted, selected, color }: TagLabelProps) => {
  const [measuredWidth, setMeasuredWidth] = React.useState(TAG_LABEL_INITIAL_WIDTH);
  const labelRadius = highlighted ? ringOuterRadius : ringInnerRadius;
  const { labelX, labelY, offsetX, offsetY, showLabel } = computeTagLabelPosition(
    angles,
    labelRadius,
    TAG_LABEL_INITIAL_WIDTH,
    measuredWidth,
    selected,
    highlighted
  );
  const refHandler = React.useCallback(createLabelRefHandler(setMeasuredWidth), [setMeasuredWidth]);

  if (!showLabel) return null;

  return (
    <foreignObject
      aria-hidden="true"
      className={tagLabelClassNames.root}
      width={1}
      height={1}
      x={labelX}
      y={labelY}
      style={
        {
          position: 'relative',
          overflow: 'visible',
          '--offset-x': `${offsetX}px`,
          '--offset-y': `${offsetY}px`,
        } as React.CSSProperties
      }>
      <StyledCustomChartLabel className={tagLabelClassNames.content} active={selected || highlighted} labelColor={color} ref={refHandler}>
        {label}
      </StyledCustomChartLabel>
    </foreignObject>
  );
};

const StyledCustomChartLabel = styled(CustomChartLabel)(() => ({
  transform: `translate(calc(-50% + var(--offset-x, 0px)), calc(-50% + var(--offset-y, 0px)))`,
}));
